// Libraries
import { CommonModule } from '@angular/common';
import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatNativeDateModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MAT_DIALOG_DATA, MatDialog, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatTabsModule } from '@angular/material/tabs';
import { MatToolbarModule } from '@angular/material/toolbar';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

// Services
import { ActionService } from 'src/app/services/api/actions/action.service';
import { MetricService } from 'src/app/services/api/metrics/metric.service';
import { UserService } from 'src/app/services/api/users/user.service';
import { AuthService } from 'src/app/services/core/auth.service';

// Interfaces
import {
  Action,
  ActionEffectiveness,
  ActionPriority,
  ActionStatus,
  ActionType,
  defaultAction,
  PostAction,
  PutAction,
} from 'src/app/interfaces/api/action';
import { Metric } from 'src/app/interfaces/api/metric';
import { User } from 'src/app/interfaces/api/user';

// Classes
import { Pop } from 'src/app/classes/shared/pop';
import { TimeFormatPipe } from 'src/app/pipes/time-format.pipe';

// Components
import { ButtonComponent } from 'ui-library';

@Component({
  selector: 'app-action-dialog',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatToolbarModule,
    MatButtonModule,
    MatIconModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatDividerModule,
    MatTabsModule,
    TranslateModule,
    TimeFormatPipe,
    ButtonComponent,
  ],
  templateUrl: './action-dialog.component.html',
  styleUrls: ['./action-dialog.component.scss'],
})
export class ActionDialogComponent implements OnInit, OnDestroy {
  public readonly data: any = inject(MAT_DIALOG_DATA);
  private readonly dialogRef: MatDialogRef<ActionDialogComponent> = inject(MatDialogRef);
  private readonly actionService: ActionService = inject(ActionService);
  private readonly userService: UserService = inject(UserService);
  private readonly metricService: MetricService = inject(MetricService);
  private readonly authService: AuthService = inject(AuthService);
  private readonly translate: TranslateService = inject(TranslateService);
  private readonly dialog: MatDialog = inject(MatDialog);
  private readonly pop: Pop = inject(Pop);

  action: Partial<Action> = { ...defaultAction };
  users: User[] = [];
  risks: Metric[] = [];
  destroy$ = new Subject<void>();
  saving: boolean = false;

  initialStatus?: ActionStatus;
  statusChangeComment: string = '';

  typeOptions: ActionType[] = ['corrective', 'preventive', 'improvement'];
  priorityOptions: ActionPriority[] = ['low', 'medium', 'high', 'critical'];
  statusOptions: ActionStatus[] = ['todo', 'in_progress', 'done', 'closed', 'cancelled'];
  effectivenessOptions: ActionEffectiveness[] = ['pending', 'effective', 'partially_effective', 'ineffective'];
  sourceOptions = ['risk', 'manual', 'audit', 'other'];

  ngOnInit() {
    this.action = { ...defaultAction, ...(this.data.action || {}) };
    this.initialStatus = this.action.status as ActionStatus;

    let assigneeObj: User | null = null;
    if (this.action.assignee && typeof this.action.assignee === 'object') {
      assigneeObj = this.action.assignee as User;
      this.action.assignee = (assigneeObj as any).id || (assigneeObj as any)._id;
    }
    const legacyRisk = (this.action as any).risk;
    if (!this.action.risks?.length && legacyRisk) {
      const legacyId = typeof legacyRisk === 'object' && 'id' in legacyRisk ? legacyRisk.id : legacyRisk;
      this.action.risks = legacyId ? [legacyId] : [];
    }
    if (this.action.risks?.length) {
      this.action.risks = this.action.risks.map((r: any) =>
        r && typeof r === 'object' ? (r.id || r._id) : r
      );
    } else {
      this.action.risks = [];
    }

    if (this.data.projectId && !this.action.project) {
      this.action.project = this.data.projectId;
    }

    this.loadUsers(assigneeObj);
    this.loadRisks();
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private loadUsers(assigneeObj?: User | null) {
    if (!this.data.projectId) return;
    this.userService.getUsersLight(this.data.projectId).pipe(takeUntil(this.destroy$)).subscribe({
      next: (res: User[]) => {
        this.users = res || [];
        // Keep the current assignee selectable even if absent from the light list
        if (assigneeObj && this.action.assignee && !this.users.some(u => u.id === this.action.assignee)) {
          this.users = [assigneeObj, ...this.users];
        }
      },
      error: () => this.users = [],
    });
  }

  private loadRisks() {
    const query: any = { name: 'Risque', limit: 500 };
    this.metricService.getMetrics(query).pipe(takeUntil(this.destroy$)).subscribe({
      next: (res: Metric[]) => this.risks = res || [],
      error: () => this.risks = [],
    });
  }

  isStatusChanged(): boolean {
    return this.data.mode === 'edit' && this.initialStatus !== this.action.status;
  }

  private titlesForRiskIds(ids: string[]): string[] {
    return ids
      .map(id => this.risks.find(r => r.id === id)?.payload?.title)
      .filter((t): t is string => !!t);
  }

  save() {
    if (!this.action.title || !this.action.title.trim()) {
      this.pop.error(this.translate.instant('actions.title_required'));
      return;
    }

    this.saving = true;

    if (this.data.mode === 'edit' && this.action.id) {
      const selectedRiskIds = (this.action.risks as string[] | undefined) ?? [];
      const put: PutAction = {
        id: this.action.id,
        title: this.action.title,
        description: this.action.description,
        type: this.action.type,
        priority: this.action.priority,
        status: this.action.status,
        assignee: this.action.assignee as string,
        dueDate: this.action.dueDate,
        completionDate: this.action.completionDate,
        effectiveness: this.action.effectiveness,
        effectivenessNote: this.action.effectivenessNote,
        risks: selectedRiskIds,
        riskTitles: this.titlesForRiskIds(selectedRiskIds),
      };
      if (this.isStatusChanged()) put.statusChangeComment = this.statusChangeComment;

      this.actionService.updateAction(put).pipe(takeUntil(this.destroy$)).subscribe({
        next: () => {
          this.saving = false;
          this.pop.toast(this.translate.instant('actions.save_success'), 'success', 4000, 'save');
          this.dialogRef.close(true);
        },
        error: (err) => {
          this.saving = false;
          console.error(err);
          this.pop.error(this.translate.instant('actions.save_error'));
        },
      });
    } else {
      const selectedRiskIds = (this.action.risks as string[] | undefined) ?? [];
      const post: PostAction = {
        project: this.action.project as string,
        title: this.action.title!,
        description: this.action.description,
        type: this.action.type as ActionType,
        priority: this.action.priority as ActionPriority,
        status: this.action.status as ActionStatus,
        source: this.action.source as any,
        assignee: this.action.assignee as string,
        dueDate: this.action.dueDate,
        risks: selectedRiskIds,
        riskTitles: this.titlesForRiskIds(selectedRiskIds),
      };

      this.actionService.addAction(post).pipe(takeUntil(this.destroy$)).subscribe({
        next: () => {
          this.saving = false;
          this.pop.toast(this.translate.instant('actions.create_success'), 'success', 4000, 'save');
          this.dialogRef.close(true);
        },
        error: (err) => {
          this.saving = false;
          console.error(err);
          this.pop.error(this.translate.instant('actions.save_error'));
        },
      });
    }
  }

  close() {
    this.dialogRef.close(false);
  }
}
